'use client';

import React, {
  cloneElement,
  DetailedHTMLProps,
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useState,
} from 'react';

import { v4 as uuidv4 } from 'uuid';

import cn from '@/helpers/utils/classNames';

export interface RadioProps
  extends Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'children' | 'type' | 'value' | 'checked' | 'onChange'
  > {
  currentValue?: string;
  defaultValue: string;
  children: ReactNode;
  onChange?: (value: string) => void;
  containerClassName?: string;
  onMount?: () => void;
  labelClassName?: string;
}

export default forwardRef<HTMLInputElement, RadioProps>(function Radio(props, ref) {
  const {
    children,
    className,
    currentValue,
    defaultValue,
    onMount,
    containerClassName,
    disabled,
    labelClassName,
    onChange,
    ...restProps
  } = props;

  const isChecked = defaultValue === currentValue;

  const inputId = uuidv4();

  useEffect(() => {
    if (onMount) onMount();
  }, [onMount]);

  return (
    <div className={cn('flex w-auto items-center', containerClassName)}>
      <input
        id={inputId}
        ref={ref}
        className={cn(
          "relative mr-2 h-4 min-h-[1rem] w-4 min-w-[1rem] appearance-none rounded-full border border-solid border-grayscale-90 duration-300 before:pointer-events-none before:absolute before:h-4 before:w-4 before:scale-0 before:rounded-full before:bg-transparent before:opacity-0 before:content-[''] after:absolute after:z-[1] after:block after:h-4 after:w-4 after:rounded-full after:content-[''] after:[transform:translate(-50%,-50%)] checked:border-primary checked:before:opacity-[0.16] checked:after:absolute checked:after:left-1/2 checked:after:top-1/2 checked:after:h-[7px] checked:after:w-[7px] checked:after:-translate-x-1/2 checked:after:-translate-y-1/2 checked:after:rounded-full checked:after:border-primary checked:after:bg-primary checked:after:content-[''] hover:cursor-pointer hover:shadow-none hover:outline-none hover:ring-0 hover:before:scale-75 hover:before:rounded-full hover:before:opacity-[0.12] hover:before:shadow-[-2px_-2px_0px_10px_#3b71ca] hover:before:transition-[box-shadow_0.4s,transform_0.4s] checked:focus:border-primary disabled:pointer-events-none",
          className
        )}
        type="radio"
        value={defaultValue}
        checked={isChecked}
        onChange={() => onChange && onChange(defaultValue)}
        {...restProps}
      />
      <label className={cn('h-full cursor-pointer', labelClassName)} htmlFor={inputId}>
        {children}
      </label>
    </div>
  );
});

export type RadioGroupProps = Pick<RadioProps, 'onChange' | 'name'> & {
  initialSelectedValue: string;
  children: React.ReactElement<RadioProps>[] | ReactNode;
  className?: string;
  onMount?: () => void;
};

export const RadioGroup = ({ children, onChange, onMount, initialSelectedValue, name, className }: RadioGroupProps) => {
  const [currentValue, setCurrentValue] = useState<string>('0');

  useEffect(() => {
    setCurrentValue(() => initialSelectedValue);
  }, [initialSelectedValue]);

  useEffect(() => {
    if (onMount) onMount();
  }, [onMount]);

  return (
    <div key={name} className={cn('', className)}>
      {React.Children.map(children as React.ReactElement<RadioProps>[], (child: React.ReactElement<RadioProps>) =>
        child
          ? cloneElement(child, {
              ...child.props,
              currentValue,
              onChange: (value: string) => {
                setCurrentValue(value);
                onChange && onChange(value);
                child.props.onChange && child.props.onChange(value);
              },
            })
          : null
      )}
    </div>
  );
};
